import { useEffect, useState, type ReactNode } from 'react'
import { createFileRoute, useNavigate } from '@tanstack/react-router'
import { z } from 'zod'
import { createClient } from '@kairos/supabase/browser'

import { completeOAuthSignIn } from '~/lib/auth/complete-oauth-sign-in'
import { safeNextPath } from '~/lib/auth/oauth-redirect'
import { logger } from '~/lib/logger'

/**
 * `/authentication/oauth-callback` — handoff splash for OAuth providers.
 *
 * The shared browser client automatically exchanges the PKCE code and removes
 * it from the URL. We verify the established user and platform-admin access,
 * then route to `next` (validated as same-origin to prevent open-redirect) or
 * `/admin`.
 *
 * Per `02-authentication.md` Sub-Surface 10, the page itself renders a
 * brief calm splash — no form, no chrome other than the centered card
 * already provided by the parent `/authentication` layout.
 */
const searchSchema = z.object({
  code: z.string().optional(),
  next: z.string().optional(),
})

type SearchParams = z.infer<typeof searchSchema>

export const Route = createFileRoute('/authentication/oauth-callback')({
  validateSearch: searchSchema,
  component: OAuthCallback,
})

function OAuthCallback(): ReactNode {
  const search = Route.useSearch() as SearchParams
  const navigate = useNavigate()
  const [error, setError] = useState<string | null>(null)

  useEffect(() => {
    let cancelled = false

    async function exchange(): Promise<void> {
      try {
        const supabase = createClient()
        const result = await completeOAuthSignIn(supabase)

        if (result.status === 'denied') {
          if (!cancelled) {
            setError('This account does not have admin access.')
          }
          return
        }

        if (result.status === 'error') {
          logger.warn('oauth completion failed', {
            reason: 'session-or-admin-verification',
          })
          if (!cancelled) {
            setError("We couldn't sign you in. Try again.")
          }
          return
        }

        // CASA 3.3.1: a real admin whose session is only aal1 — which is every fresh
        // OAuth sign-in, since the provider cannot satisfy a TOTP factor — must be sent
        // to the TOTP challenge/enrol surface on the session that was just established.
        // Without this branch the code fell through to `navigate(target)` with the
        // session already cleared, so Google sign-in bounced straight back to the login
        // page. Mirrors `authentication.login.tsx`.
        if (result.status === 'mfa_required') {
          if (!cancelled) {
            navigate({ to: '/authentication/mfa' })
          }
          return
        }
      } catch (cause: unknown) {
        const message = cause instanceof Error ? cause.message : 'Unknown error'
        logger.warn('oauth completion threw', { message })
        if (!cancelled) setError("We couldn't sign you in. Try again.")
        return
      }

      if (cancelled) return
      const target = safeNextPath(search.next)
      navigate({ to: target })
    }

    void exchange()
    return () => {
      cancelled = true
    }
  }, [search.next, navigate])

  if (error) {
    return (
      <div role="alert" className="ka-auth-error-block">
        <h1 className="ka-auth-error-block-title">Sign-in failed</h1>
        <p className="ka-auth-error-block-msg">{error}</p>
        <p className="ka-auth-error-block-foot">
          <a href="/authentication/login">Back to sign in</a>
        </p>
      </div>
    )
  }

  return (
    <div role="status" aria-live="polite" className="ka-auth-splash">
      <span aria-hidden="true" className="ka-auth-splash-spinner" />
      <h1 className="ka-auth-splash-title">Completing sign-in…</h1>
      <p className="ka-auth-splash-sub">This will only take a moment.</p>
    </div>
  )
}
